/**
 * Edit one provider's DEFAULT VALUES.
 *
 * Mirrors `ModelDefaultsDialog` one tier up: fingerprint echo, impact panel
 * before the write, null clears a key. The schema is resolved against any model
 * on the provider so the dialog has a param grid to render; the WRITE itself is
 * provider-scoped and reaches every non-overriding entry.
 *
 * Open to any account member — this is a preference surface, not an
 * admin gate. The capability SPEC editor stays on the Providers tab separately.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@localagent/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@localagent/ui/components/dialog';
import { isConflict } from '@localagent/sdk';
import type { ParamSchema, ProviderType, SupportedParamSpec } from '@localagent/sdk';
import { useParamSchema } from '../../hooks/useAgentDefinitions';
import { useDefaultOverrides, useProviderModels, useSetProviderParamDefaults } from '../../hooks/useProviderManagement';
import { SchemaStaleNotice } from '../agents/AgentParamNotices';
import { DefaultsImpactPanel, ModelDefaultsRows } from './ModelDefaultsDialog';

function parseValue(text: string, spec: SupportedParamSpec): number | string {
  if (spec.type === 'integer') return parseInt(text, 10);
  if (spec.type === 'numeric') return parseFloat(text);
  return text;
}

/** Keys this PROVIDER owns, reconstructed from the resolved schema. */
function providerTierDefaults(schema: ParamSchema): Record<string, unknown> {
  const own: Record<string, unknown> = {};
  for (const [key, source] of Object.entries(schema.defaults_source)) {
    if (source === 'provider') own[key] = schema.defaults[key];
  }
  return own;
}

export interface ProviderDefaultsDialogProps {
  providerType: ProviderType;
  open: boolean;
  onClose: () => void;
}

export function ProviderDefaultsDialog({
  providerType,
  open,
  onClose,
}: ProviderDefaultsDialogProps) {
  const modelsQuery = useProviderModels(open ? providerType : null);
  // Any model on the provider is enough to resolve the shared param grid; the
  // write is provider-scoped regardless of which entry supplied the schema.
  const anchorModel = modelsQuery.data?.models[0]?.name;
  const schemaQuery = useParamSchema(
    open && anchorModel ? providerType : undefined,
    open && anchorModel ? anchorModel : undefined,
  );
  const schema = schemaQuery.data;
  const setDefaults = useSetProviderParamDefaults();

  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState('');
  const [schemaStale, setSchemaStale] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft({});
    setSaveError('');
    setSchemaStale(false);
  }, [open, providerType]);

  const changedKeys = useMemo(() => Object.keys(draft), [draft]);

  // Provider-tier impact: omit modelName so every model on the provider counts.
  const impact = useDefaultOverrides(
    open ? providerType : undefined,
    changedKeys,
  );

  const handleSave = () => {
    if (!schema) return;
    setSaveError('');
    setSchemaStale(false);

    const next = providerTierDefaults(schema);
    for (const [key, text] of Object.entries(draft)) {
      const spec = schema.params[key];
      if (!spec) continue;
      if (text.trim() === '') {
        next[key] = null;
        continue;
      }
      const parsed = parseValue(text, spec);
      if (typeof parsed === 'number' && Number.isNaN(parsed)) {
        setSaveError(`${spec.label}: enter a number`);
        return;
      }
      next[key] = parsed;
    }

    setDefaults.mutate(
      {
        providerType,
        defaults: next,
        fingerprint: schema.fingerprint,
      },
      {
        onSuccess: () => onClose(),
        onError: error => {
          if (isConflict(error)) {
            setSchemaStale(true);
            return;
          }
          setSaveError(error.message);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={value => { if (!value) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="provider-defaults-dialog">
        <DialogHeader>
          <DialogTitle>Provider default values — {providerType}</DialogTitle>
          <DialogDescription>
            Defaults every model entry inherits unless it overrides them. Changing one
            reaches every agent that has not pinned it, on its next turn.
          </DialogDescription>
        </DialogHeader>

        {schemaStale && (
          <SchemaStaleNotice
            onReload={() => {
              setSchemaStale(false);
              void schemaQuery.refetch();
            }}
          />
        )}

        {schemaQuery.isError && (
          <p data-testid="provider-defaults-schema-error" className="text-body text-destructive">
            {schemaQuery.error.message}
          </p>
        )}

        {!anchorModel && !modelsQuery.isLoading && (
          <p className="text-body text-muted-foreground">
            Add a model entry for this provider before editing its defaults.
          </p>
        )}

        {schema && (
          <ModelDefaultsRows
            schema={schema}
            draft={draft}
            onDraftChange={(key, text) => setDraft(prev => ({ ...prev, [key]: text }))}
            disabled={setDefaults.isPending}
          />
        )}

        {changedKeys.length > 0 && impact.data && (
          <DefaultsImpactPanel
            count={impact.data.count}
            personaNames={impact.data.personas.map(p => p.persona_name)}
            defaultsVersion={impact.data.defaults_version}
          />
        )}

        {saveError && (
          <p data-testid="provider-defaults-error" className="text-body text-destructive">
            {saveError}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={setDefaults.isPending}>
            Cancel
          </Button>
          <Button
            data-testid="provider-defaults-save"
            onClick={handleSave}
            disabled={!schema || changedKeys.length === 0 || setDefaults.isPending}
          >
            {setDefaults.isPending ? 'Saving…' : 'Save defaults'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
